<template>
    <div id="page-tasks" class="pl-3">
        <div class="top filter-bar-container">
            <div class="title-button">
                <h1>{{ t("tasks_report-page_title") }}</h1>
                <router-link class="btn btn-primary" to="/tasks/new">
                    <Icon icon-class="ui-plus" size="5" />
                    <span>{{ t("tasks_report-add_button") }}</span>
                </router-link>
            </div>
            <filter-bar
                ref="filterBar"
                :has-period-filter="false"
                :has-branch-filter="canFilterPerBranch"
                :has-client-filter="false"
                :show-labels="true"
                @changed="updateFilters"
                :filters="extraFilters"
            />
        </div>
        <div class="content box" v-if="loading">
            <loader />
        </div>

        <template v-else-if="report.created > 0">
            <div class="row stats top-stats">
                <div class="col-sm-12 col-md-4">
                    <div class="box">
                        <div class="icon">
                            <Icon icon-class="ui-list" size="13" />
                        </div>
                        <div class="info">
                            <div class="number">{{ report.created }}</div>
                            <div class="label">
                                {{ t("tasks_report-created_tasks") }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 col-md-4">
                    <div class="box">
                        <div class="icon">
                            <Icon icon-class="ui-check-square-broken" size="13" />
                        </div>
                        <div class="info">
                            <div class="number">
                                {{ report.completed }}
                                <small
                                    >({{
                                        report.created > 0
                                            ? formatNumber(
                                                  (report.completed /
                                                      report.created) *
                                                      100,
                                              ) + "%"
                                            : "0%"
                                    }})</small
                                >
                            </div>
                            <div class="label">
                                {{ t("tasks_report-completed_tasks") }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 col-md-4">
                    <div class="box">
                        <div class="icon">
                            <Icon icon-class="ui-clock-check" size="13" />
                        </div>
                        <div class="info">
                            <div class="number">
                                {{ report.completedOnTime }}
                                <small
                                    >({{
                                        report.completed > 0
                                            ? formatNumber(
                                                  (report.completedOnTime /
                                                      report.created) *
                                                      100,
                                              ) + "%"
                                            : "0%"
                                    }})</small
                                >
                            </div>
                            <div class="label">
                                {{ t("tasks_report-on_time_tasks") }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row stats top-stats">
                <div class="col-sm-12 col-md-4">
                    <div class="box">
                        <div class="icon">
                            <Icon icon-class="ui-flag-01" size="13" />
                        </div>
                        <div class="info">
                            <div class="number">
                                {{ formatNumber(report.converted, 0) }}
                            </div>
                            <div class="label">
                                {{ t("tasks_report-conversions") }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 col-md-4">
                    <div class="box">
                        <div class="icon">
                            <Icon icon-class="ui-percent-03" size="13" />
                        </div>
                        <div class="info">
                            <div class="number">
                                {{ formatNumber(report.conversionRate * 100) }}%
                            </div>
                            <div class="label">
                                {{ t("tasks_report-conversion_rate") }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 col-md-4">
                    <div class="box">
                        <div class="icon">
                            <Icon icon-class="ui-coins-stacked-03" size="13" />
                        </div>
                        <div class="info">
                            <div class="number">
                                {{ currencySymbol }}{{
                                    formatNumber(report.incentivizedIncome, 2)
                                }}
                            </div>
                            <div class="label">
                                {{
                                    t(
                                        "tasks_report-incentivized_revenue",
                                    )
                                }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-12 col-md-12">
                    <div class="box">
                        <h2>{{ t("tasks_report-by_user") }}</h2>
                        <table
                            class="table table-stripped table-responsive"
                            style="width: 100%"
                        >
                            <thead>
                                <tr style="font-size: 14px">
                                    <th>{{ t("tasks_report-name") }}</th>
                                    <th>{{ t("tasks_report-created") }}</th>
                                    <th>
                                        {{ t("tasks_report-completed") }}
                                    </th>
                                    <th style="min-width: 200px">
                                        {{ t("tasks_report-on_time") }}
                                    </th>
                                    <th>
                                        {{ t("tasks_report-conversions") }}
                                    </th>
                                    <th>
                                        {{
                                            t(
                                                "tasks_report-incentivized_revenue",
                                            )
                                        }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="user in report.users" :key="user.id">
                                    <td>
                                        {{ user.name }}
                                    </td>
                                    <td>
                                        {{ user.createdTasks }}
                                    </td>
                                    <td>
                                        {{ user.completedTasks }}
                                        <small
                                            >{{
                                                user.createdTasks
                                                    ? formatNumber(
                                                          (user.completedTasks /
                                                              user.createdTasks) *
                                                              100,
                                                      )
                                                    : 0
                                            }}%</small
                                        >
                                    </td>
                                    <td>
                                        {{ user.completedOnTime }}
                                        <small
                                            >{{
                                                user.completedTasks
                                                    ? formatNumber(
                                                          (user.completedOnTime /
                                                              user.createdTasks) *
                                                              100,
                                                      )
                                                    : 0
                                            }}%</small
                                        >
                                    </td>
                                    <td>
                                        {{ user.convertedTasks }}
                                        <small
                                            >{{
                                                formatNumber(
                                                    user.conversionRate * 100,
                                                )
                                            }}%</small
                                        >
                                    </td>
                                    <td>
                                        {{ currencySymbol }}{{
                                            formatNumber(
                                                user.incentivizedIncome,
                                            )
                                        }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col-sm-12 col-md-12">
                    <div class="box">
                        <h2>{{ t("tasks_report-by_automation") }}</h2>
                        <table
                            class="table table-stripped table-responsive"
                            style="width: 100%"
                        >
                            <thead>
                                <tr style="font-size: 14px">
                                    <th>{{ t("tasks_report-name") }}</th>
                                    <th>{{ t("tasks_report-created") }}</th>
                                    <th>
                                        {{ t("tasks_report-completed") }}
                                    </th>
                                    <th style="min-width: 200px">
                                        {{ t("tasks_report-on_time") }}
                                    </th>
                                    <th>
                                        {{ t("tasks_report-conversions") }}
                                    </th>
                                    <th>
                                        {{
                                            t(
                                                "tasks_report-incentivized_revenue",
                                            )
                                        }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="automation in report.automations"
                                    :key="automation.id"
                                >
                                    <td
                                        style="
                                            width: 150px;
                                            line-height: 1.3em;
                                            font-size: 13px;
                                        "
                                    >
                                        {{ automation.name }}
                                    </td>
                                    <td>
                                        {{ automation.createdTasks }}
                                    </td>
                                    <td>
                                        {{ automation.completedTasks }}
                                        <small
                                            >{{
                                                automation.createdTasks
                                                    ? formatNumber(
                                                          (automation.completedTasks /
                                                              automation.createdTasks) *
                                                              100,
                                                      )
                                                    : 0
                                            }}%</small
                                        >
                                    </td>
                                    <td>
                                        {{ automation.completedOnTimeTasks }}
                                        <small
                                            >{{
                                                automation.completedTasks
                                                    ? formatNumber(
                                                          (automation.completedOnTimeTasks /
                                                              automation.completedTasks) *
                                                              100,
                                                      )
                                                    : 0
                                            }}%</small
                                        >
                                    </td>
                                    <td>
                                        {{ automation.convertedTasks }}
                                        <small
                                            >{{
                                                formatNumber(
                                                    automation.conversionRate *
                                                        100,
                                                )
                                            }}%</small
                                        >
                                    </td>
                                    <td>
                                        {{ currencySymbol }}{{
                                            formatNumber(
                                                automation.incentivizedIncome,
                                            )
                                        }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <h2>{{ t("tasks_report-no_data_for_period") }}</h2>
        </template>
    </div>
</template>
<script>
import keviApi from "../keviApi";
import HumanDate from "../components/human-date/human-date";
import { mapState } from "vuex";
import BtnWhatsapp from "../components/btn-whatsapp/btn-whatsapp";
import Icon from "../components/icon/icon";

export default {
    components: { BtnWhatsapp, HumanDate, Icon },
    data() {
        return {
            report: {
                created: 0,
                completed: 0,
                completedOnTime: 0,
                conversionRate: 0,
                incentivizedIncome: 0,
                users: {},
                automations: {},
            },
            loading: false,
            automations: [],
            filters: [],
            extraFilters: [],
        };
    },
    async mounted() {
      await this.loadAutomations();
      if (this.automations.length > 0) {
        this.extraFilters.push({
          component: "checkbox",
          label: this.$root.t('filter_bar-automation'),
          name: "automation_id",
          options: {
            options: [...this.automations].map((automation) => {
              return {
                value: automation.id,
                label: automation.title,
              };
            }),
            icon: "icon icon-robot-arm"
          },
          removable: true,
        });
      }

      this.$refs.filterBar.addFilter("task-created-date-period");

      this.extraFilters.push({
        component: "period",
        label: this.$root.t('filter_bar-task_deadline'),
        name: "task-due-date-period",
        removable: true,
      });
      this.extraFilters.push({
        component: "period",
        label: this.$root.t('filter_bar-creation_date'),
        name: "task-created-date-period",
        removable: false,
      });
      this.extraFilters.push({
        component: "period",
        label: this.$root.t('filter_bar-completion_date'),
        name: "task-completed-date-period",
        removable: true,
      });


    },
    computed: {
        ...mapState({
            user: (state) => state.user,
        }),
        ...mapState("placeDetails", ["users", "roles", "branches"]),
        placeId() {
            return this.$store.state.place.id;
        },
        canFilterPerBranch() {
            let inbox = this.user.permissions.inbox;
            return (
                parseInt(inbox.enabled) === 1 &&
                parseInt(inbox.options.global_access) === 1
            );
        },
        hasBranchFilter() {
            return true;
        },
        hasClientFilter() {
            return true;
        },
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        async loadAutomations() {
          const options = keviApi.getOptions("tasks/automations", "GET");
          this.automations = (await this.$http(options)).body;
        },
        updateFilters: function (filters) {
            this.filters = filters;
            this.loadReport(this.filters);
        },
        formatNumber(number, decimals = 2) {
            return parseFloat(number).toLocaleString("pt-BR", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            });
        },
        loadReport(filters) {
            this.loading = true;
            this.filters = Object.assign({}, filters);

            const options = keviApi.getOptions("tasks/report", "GET", {
                filters,
            });
            this.$http(options).then((response) => {
                this.loading = false;
                this.report = response.body.report;
            });
            localStorage.setItem("filters", JSON.stringify(this.filters));
        },
        nl2br(str) {
            if (!str) return "";
            return (str + "").replace(
                /([^>\r\n]?)(\r\n|\n\r|\r|\n)/g,
                "$1" + "<br />" + "$2",
            );
        },
    },
    watch: {},
};
</script>
